import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Redirect, Route, Switch, useRouteMatch } from "react-router";
import { KButton } from "@kyburz/kdesign-system";

import useSetState from "../../../hooks/useTypedSetState";
import { Config } from "../Config";
import BaseLayout from "../../../components/layouts/BaseLayout";
import CompaniesTab from "../../vehicleTemplates/components/tabs/CompaniesTab";
import ConfigEditingTab from "./editing-tab/ConfigEditingTab";
import { useStyles } from "./styles";

interface Props {
  title: string;
  initialConfig: Config;
  handleSave?: (config: Config) => void;
  handleCancel?: () => void;
  isLoading?: boolean;
  isCreating?: boolean;
}

const DeviceConfigDetails: React.FC<Props> = ({
  title,
  initialConfig,
  handleSave,
  handleCancel,
  isLoading,
  isCreating = false,
}: Props) => {
  const classes = useStyles();
  const history = useHistory();
  const [fileName, setFileName] = useState("");

  const match = useRouteMatch<{ configId?: string; tab?: string }>({
    path: "/config/devices/config/:configId/:tab",
    strict: true,
    sensitive: true,
  });

  const { configId, tab } = match?.params || {};

  const [{ activeTab }, setState] = useSetState({ activeTab: tab });

  const [configData, setConfigData] = useSetState(initialConfig);

  useEffect(() => {
    setState({ activeTab: tab });
  }, [setState, tab]);

  const handleTabClick = (_, newValue) => {
    history.push(`/config/devices/config/${configId}/${newValue}`);
  };

  const onSave = async () => {
    handleSave?.(configData);
  };

  return (
    <BaseLayout
      title={title}
      handleArrowClick={handleCancel}
      activeTab={activeTab}
      tabs={[
        { label: "Config Editor", value: "editor" },
        { label: "Companies", value: "companies" },
      ]}
      handleTabChange={handleTabClick}
      mainClassname={classes.layout}
    >
      <div className={classes.main}>
        <Switch>
          <Route path="/config/devices/config/:configId/editor" exact>
            <ConfigEditingTab
              config={configData}
              setConfig={setConfigData}
              selectedFileName={fileName}
              setSelectedFileName={setFileName}
            />
          </Route>
          <Route path="/config/devices/config/:configId/companies">
            <CompaniesTab
              title={`Device config ${configData.version}`}
              selectedCompanies={configData.allowedCustomers || []}
              onCompaniesChange={(companies) =>
                setConfigData({ allowedCustomers: companies })
              }
            />
          </Route>
          <Redirect to="/config/devices/config/:configId/editor" />
        </Switch>
      </div>

      <footer className={classes.buttons}>
        <KButton children="Cancel" theme="secondary" onClick={handleCancel} />

        <KButton
          children="Save and close"
          onClick={onSave}
          disabled={
            !configData.version ||
            !configData.simType ||
            (isCreating && !configData.contentsBase64) ||
            isLoading
          }
        />
      </footer>
    </BaseLayout>
  );
};

export default DeviceConfigDetails;
